import React, { useCallback, useMemo } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { LaunchpadRow } from 'pages/LaunchpadModal/subcomponents/LaunchpadRow'

import { BottomSheetNavbar } from 'partials/BottomSheetNavbar'

import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Pressable } from 'widgets/Pressable'
import { ScrollView } from 'widgets/ScrollView'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'

import { useClientSubscriptions } from 'hooks/useClientSubscriptions'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useGroup } from 'hooks/useGroup'
import { useGroups } from 'hooks/useGroups'
import { useMultipleUsers } from 'hooks/useMultipleUsers'
import { useRole } from 'hooks/useRole'

import commonStyles from 'utils/styles/commonStyles'

import { useCoachOnboardingModal } from 'contexts/CoachOnboardingModalContext'
import { useFocusMode } from 'contexts/FocusModeContext'
import { useLaunchpadModal } from 'contexts/LaunchpadModalContext'

import ExploreThumbnail from 'assets/images/explore-thumbnail.png'
import FriendsThumbnail from 'assets/images/friends-thumbnail.png'

import { Program } from 'types/program'

export const LaunchpadPage = ({ ...rest }) => {
  const insets = useSafeAreaInsets()
  const focusMode = useFocusMode()
  const launchpadModalRef = useLaunchpadModal()
  const coachOnboardingModalRef = useCoachOnboardingModal()

  const { isProgramsFlagEnabled } = useFeatureFlags()

  const role = useRole()
  const { data: currentUser } = useCurrentUser()

  const {
    meta,
    data: userGroup,
    isLoading: isUserGroupLoading,
  } = useGroup({ userId: currentUser?.id })
  const { hasSetupGroup } = meta

  const showUserProgram = !isUserGroupLoading && userGroup && hasSetupGroup

  const { data: userSubscriptions, isLoading: isUserSubscriptionsLoading } = useClientSubscriptions(
    { userId: currentUser?.id },
  )

  const handleClose = () => {
    launchpadModalRef?.current?.dismiss()
  }

  const handleCreate = useCallback(() => {
    launchpadModalRef?.current?.dismiss()
    coachOnboardingModalRef?.current?.present({
      skipSelectionPage: false, // role === 'trainer', // todo: - update this when ready
    })
  }, [coachOnboardingModalRef, launchpadModalRef])

  const handleCreateGroup = useCallback(() => {
    coachOnboardingModalRef?.current?.present()
  }, [coachOnboardingModalRef])

  const groupIds = useMemo(() => {
    const pIds = userSubscriptions?.map((sub) => sub?.program?.id) ?? []
    return showUserProgram ? [userGroup?.id, ...pIds] : pIds
  }, [userSubscriptions, showUserProgram, userGroup?.id])

  const { data: groups, isLoading: isGroupsLoading } = useGroups(groupIds)

  const trainerIds = groups?.map((p) => p?.trainer?.id) as string[]
  const { data: trainers, isLoading: isTrainersLoading } = useMultipleUsers({ userIds: trainerIds })

  const handleGroupSelect = (program: Program | null) => {
    if (!program) return
    focusMode.setGroup(program)
    launchpadModalRef?.current?.dismiss()
  }

  const isLoading =
    isUserSubscriptionsLoading || isUserGroupLoading || isGroupsLoading || isTrainersLoading

  return (
    <Box flex={1} backgroundColor='white' align='center' padding={{ bottom: insets.bottom }}>
      <BottomSheetNavbar title='Switch Groups' boldTitle={false} onClose={handleClose} />

      <Box flex={1} width='100%'>
        <Box flex={1}>
          <ScrollView contentContainerStyle={{ paddingVertical: 12 }}>
            {isProgramsFlagEnabled && (
              <>
                <LaunchpadRow
                  type='circle'
                  title='Home'
                  subTitle='My Stuff'
                  image={currentUser?.avatar}
                  onPress={() => {}}
                />
                <LaunchpadRow
                  title='Explore'
                  subTitle='Discover here'
                  image={ExploreThumbnail}
                  onPress={() => {}}
                />
                <LaunchpadRow
                  title='Friends'
                  subTitle='Users you follow who follow you back'
                  image={FriendsThumbnail}
                  onPress={() => {}}
                />
                <Box padding={{ horizontal: 'xl' }} margin={{ vertical: 'sm', horizontal: 'lg' }}>
                  <Separator />
                </Box>
              </>
            )}
            {isLoading ? (
              <Box margin='xl' padding='xl' justifyContent='center' align='center'>
                <LoadingSpinner />
              </Box>
            ) : (
              <>
                {groups?.map((program, index) => (
                  <LaunchpadRow
                    key={program?.id}
                    title={
                      program?.title ??
                      (trainers
                        ? `${trainers[index]?.firstName} ${trainers[index]?.lastName}'s Group`
                        : '')
                    }
                    subTitle={
                      trainers ? `${trainers[index]?.firstName} ${trainers[index]?.lastName}` : ''
                    }
                    image={program?.cover ?? (trainers ? trainers[index]?.avatar : undefined)}
                    isSelected={program?.id === focusMode?.group?.id}
                    onPress={() => handleGroupSelect(program)}
                  />
                ))}
              </>
            )}
          </ScrollView>
        </Box>
      </Box>

      {(role !== 'trainer' || isProgramsFlagEnabled) && (
        <Box
          height={50}
          width='100%'
          align='center'
          justifyContent='center'
          margin={{ vertical: 'xl' }}
          padding={{ horizontal: 'md', bottom: 'xl' }}
        >
          <Pressable
            onPress={isProgramsFlagEnabled ? handleCreate : handleCreateGroup}
            style={commonStyles.fullWidth}
          >
            <Box
              height={40}
              flexDirection='row'
              align='center'
              borderRadius='lg'
              padding={{ horizontal: 'sm' }}
              border={{ color: 'v2primary', width: 1 }}
            >
              <Icon name='plus' color='v2primary' />
              <Box flex={1} align='center' justifyContent='center'>
                <Text color='v2primary' align='center' numberOfLines={1}>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {!isProgramsFlagEnabled
                    ? 'Create a Group'
                    : role !== 'trainer'
                    ? 'Create Group or Program'
                    : 'Create Program'}
                </Text>
              </Box>
            </Box>
          </Pressable>
        </Box>
      )}
    </Box>
  )
}
